import { Box, Grid } from '@mui/material';
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { fetchKnowledgeQuickAccess } from 'app/features/menu/menuSlice';
import { store } from 'app/store';
import { MprcButton, MprcConfirmDialog, MprcContainer, MprcInfiniteLoading, MprcRequestErrorDialog, MprcRequestIndicator, MprcStringToHtml } from 'components';
import { MprcResolveRequestErrors } from 'components/MprcRequestErrorReport';
import { ButtonSave } from 'components/buttons';
import { IconGroup } from 'components/icons';
import { CreateChannelPayload, ManagedChannelQueryResponse } from 'generated/CoreApi_v1';
import LibUtils from 'lib/LibUtils';
import { useSnackbar } from 'notistack';
import { useCoreApi } from 'providers/CoreApiProvider';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { KnowledgePaths } from 'routes/KnowledgeRouter';
import { NIL } from 'uuid';
import { EditChannelFooter } from './EditChannelFooter';
import { EditChannelHeader } from './EditChannelHeader';
import { EditChannelMainBox } from './EditChannelMainBox';

////////////////////////////////////////////////////////////////////////////

interface IBoxValidations {
  mainBox: boolean;
}

////////////////////////////////////////////////////////////////////////////

/**
 * Edição de Canais
 * @param props
 */
export const EditChannel: React.FC = () => {

  const api = useCoreApi();
  const navigate = useNavigate();
  const { t } = useTranslation("knowledge");
  const { enqueueSnackbar } = useSnackbar();
  const { channelId: id } = useParams();

  const [channelId, setChannelId] = useState<string | undefined>(id);
  const [viewData, setViewData] = useState<ManagedChannelQueryResponse>();
  const [loadDto, setLoadDto] = useState<ManagedChannelQueryResponse>();
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [viewError, setViewError] = useState<any>();
  const [recordChanged, setRecordChanged] = useState<boolean>(false);
  const [reloadOnClose, setReloadOnClose] = useState<boolean>(false);
  const [dialog, setDialog] = useState<any>(undefined);
  const [viewFieldsErrors, setViewFieldsErrors] = useState<ApiProblemErrors>();
  const [boxValidations, setBoxValidations] = useState<IBoxValidations>({ mainBox: false });

  const canSave = recordChanged && Object.values(boxValidations).filter((x) => x === false).length == 0;

  ////////////////////////////////////////////////////////////////////////////
  ///
  const getSaveCommand = (): CreateChannelPayload => {

    let command: CreateChannelPayload = {
      name: viewData!.name,
      description: viewData?.description ?? null,
      allowInvite: viewData!.allowInvite,
      allowNonMemberComments: viewData!.allowNonMemberComments,
      allowSupervisorEdit: viewData!.allowSupervisorEdit,
      requirePublicationEditJustification: viewData!.requirePublicationEditJustification,
      enableIaQuestionAnswering: viewData!.enableIaQuestionAnswering,
      autoRegisterMode: viewData!.autoRegisterMode,
      autoRegisterRole: viewData!.autoRegisterRole,
      denyExternalUsers: viewData!.denyExternalUsers,
      dismissApprovalForUnits: viewData!.dismissApprovalForUnits,
      enableVideoModeration: viewData!.enableVideoModeration,
      isPrivate: viewData!.isPrivate,
      containsConfidentialInfo: viewData!.containsConfidentialInfo,
      approvalMode: viewData!.approvalMode,
      preferModerationByManager: viewData!.preferModerationByManager,
      dismissApprovalWithPassword: viewData!.dismissApprovalWithPassword ? viewData?.approvalPassword ?? null : null,
      thumbnailId: viewData?.thumbnailId ?? null,
      moderatorId: viewData!.moderator.key,
      topicsSuggested: viewData!.topicsSuggested?.map(x => x.key) ?? [],
      unitsDismissedGroupIds: viewData!.unitsDismissed?.map(x => x.includeDescendants ? x.allUsersGroup.id : x.usersGroup.id) ?? [],
      channelId: null,
      useCustomQuickFilter: viewData?.useCustomQuickFilter ?? false
    };

    return command;
  };

  const createChannel = () => {
    setSaving(true);
    api.v1.channelManagement.createChannel(getSaveCommand())
      .then(result => {
        const newChannelId = result.data;
        if (!newChannelId || newChannelId == NIL) {
          enqueueSnackbar(t("Channel.Edit.SaveFailMessage"), { variant: "error" });
        } else {
          setChannelId(newChannelId);
          setDialog(
            <MprcConfirmDialog
              open
              message={t("Channel.Edit.ConfirmGoToMembersListMessage")}
              onClose={
                (yes) => {
                  store.dispatch(fetchKnowledgeQuickAccess({}));

                  if (yes) goToMembersList(newChannelId);
                  else {
                    enqueueSnackbar(t("Record.SaveSuccessMessage", { ns: "shared" }), { variant: "success" });
                    store.dispatch(fetchKnowledgeQuickAccess({}));
                  }
                  setDialog(null);
                  setLoading(true);
                  setReloadOnClose(true);
                }
              }
            />
          );
        }
      }).catch(e => {
        resolveErrors(e);
      })
      .finally(() => {
        setSaving(false);
      })
      ;
  };

  const updateChannel = () => {
    setSaving(true);
    api.v1.channelManagement.updateChannel({ ...getSaveCommand(), channelId: channelId! })
      .then(result => {
        if (!result.data) {
          enqueueSnackbar(t("Channel.Edit.SaveFailMessage"), { variant: "error" });
        } else {
          enqueueSnackbar(t("Record.SaveSuccessMessage", { ns: "shared" }), { variant: "success" });
          setLoading(true);
          setReloadOnClose(true);
        }
      }).catch(e => {
        resolveErrors(e);
      })
      .finally(() => {
        setSaving(false);
      })
      ;
  };

  const handleSave = async () => {
    if (!canSave) return;

    setSaving(true);
    if (channelId === undefined) {
      createChannel();
    } else {
      updateChannel();
    }
  };

  ///
  const goToMembersList = (channelId: string) => {
    navigate(KnowledgePaths.BuildEditChannelMembers(channelId));
  };

  ///
  const getHeaderItems = () => {
    const formHeaderOptions: React.ReactNode[] = [];

    if (channelId !== undefined) {
      formHeaderOptions.push(
        <MprcButton key="btnGroup"
          rounded
          size="large"
          variant="text"
          color="info"
          onClick={() => goToMembersList(channelId)}
          startIcon={<IconGroup />}
          disabled={loading || saving || recordChanged}
        >
          {t("Channel.Edit.MemberEditButtonText")}
        </MprcButton>
      );
    }

    formHeaderOptions.push(<ButtonSave key="btnSave" rounded size="large" variant="text" onClick={handleSave} disabled={!canSave} />);

    return formHeaderOptions;
  };

  ///
  const refreshData = (dto: ManagedChannelQueryResponse) => {
    setLoadDto(LibUtils.deepCopy(dto));
    setViewData(dto);
    setViewError(undefined);
    setViewFieldsErrors(undefined);
  };

  ///
  const resolveErrors = (e: any | unknown) => {
    var r = MprcResolveRequestErrors(e);
    setViewFieldsErrors(r.fieldsWithError);
    setViewError(r.otherErrors);
    if (r.fieldsWithError != null) enqueueSnackbar(t("Record.SaveFailedWithFieldErrorsMessage", { ns: "shared" }), { variant: "error" });
  };

  ///
  function handleUpdateViewData(newDto: ManagedChannelQueryResponse) {
    setViewData(newDto);
  }

  // Ao editar o DTO atual:
  useEffect(() => {
    const isRecordChanged = !LibUtils.deepCompare(viewData, loadDto);
    setRecordChanged(isRecordChanged);
  }, [viewData]);

  ////////////////////////////////////////////////////////////////////////////
  /// RENDER
  ////////////////////////////////////////////////////////////////////////////

  const Header = (
    <EditChannelHeader reloadOnClose={reloadOnClose} recordChanged={recordChanged} onClose={() => navigate(-1)} actions={getHeaderItems()} />
  );

  useEffect(() => {
    async function getData() {
      try {
        let result;
        if (channelId != undefined) {
          result = await api.v1.channelManagement.getChannel({ ChannelId: channelId });
        }
        else {
          result = await api.v1.channelManagement.getChannelDefault();
        }

        let data = result.data;
        if (channelId === undefined) {
          data = LibUtils.convertNullToUndefined(result.data);
        }
        refreshData(data);
      } catch (e: any) {
        setViewError(e as Error);
      }

      setLoading(false);
    }


    if (loading) getData();
  }, [loading]);

  return <Box height="100%">
    <MprcRequestIndicator
      visible={saving}
      type={"post"}
      message={
        loadDto?.isPrivate != viewData?.isPrivate
          ? <MprcStringToHtml splitChar={'\n'} text={t("Channel.Edit.PostWithPrivateChangesMessage")} />
          : undefined
      }
    />
    <MprcRequestErrorDialog error={viewError} onClose={() => setViewError(undefined)} />
    {dialog}

    <Grid container direction="column" height="100%">
      <Grid width="100%">
        {Header}
      </Grid>
      <Grid size="grow" width="100%" flexGrow={1} overflow="hidden">
        <MprcInfiniteLoading visible={loading} />
        {!loading &&
          <Grid container direction="column" height="100%">
            <Grid size="grow" overflow="auto" className={AppGlobalClassKeys.thinScrollbar}>
              <EditChannelMainBox
                viewData={viewData as ManagedChannelQueryResponse}
                viewErrors={viewFieldsErrors}
                recordChanged={recordChanged}
                onFormUpdate={handleUpdateViewData}
                onFormValidation={(valid) => setBoxValidations({ mainBox: valid })}
                onError={error => resolveErrors(error)}
              />
              {viewData?.channelId != undefined && viewData.recordInfo && (
                <MprcContainer>
                  <EditChannelFooter
                    channelId={viewData.channelId}
                    name={viewData.name}
                    recordInfo={viewData.recordInfo}
                  />
                </MprcContainer>
              )}
            </Grid>
          </Grid>
        }
      </Grid>
    </Grid>
  </Box>;
};
